import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import type { PitchMode } from '../../model/pitchVisibility';

export interface PitchModeMenuProps {
  mode: PitchMode;
  onSelect: (mode: PitchMode) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  /** `side` opens toward the score from the right-hand column.
   *  `top` opens upward from the mobile bottom bar. */
  placement: 'side' | 'top';
}

const OPTIONS: { mode: PitchMode; title: string }[] = [
  { mode: 'off', title: 'Off' },
  { mode: 'ledger', title: 'Partial' },
  { mode: 'all', title: 'All' },
];

/**
 * Three-choice menu for the Pitch pill. Dismissal matches the font-size
 * popover: Escape, and mousedown outside both the panel and the trigger.
 * A hidden duplicate (desktop column and mobile bar both mount) ignores
 * clicks while `display: none`.
 */
export const PitchModeMenu: React.FC<PitchModeMenuProps> = ({
  mode,
  onSelect,
  onClose,
  anchorRef,
  placement,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onClick = (event: MouseEvent) => {
      if (!panelRef.current || panelRef.current.offsetParent === null) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label="Pitch letters"
      className={`
        absolute z-50 w-max rounded-xl bg-white py-1 shadow-lg ring-1
        ring-black/10
        ${
          placement === 'top'
            ? 'right-0 bottom-[calc(100%+8px)]'
            : 'top-0 right-[calc(100%+8px)]'
        }
      `}
    >
      {OPTIONS.map((option) => {
        const selected = option.mode === mode;
        return (
          <button
            key={option.mode}
            type="button"
            role="menuitemradio"
            aria-checked={selected}
            onClick={() => onSelect(option.mode)}
            className={`
              flex w-full items-center gap-2 px-3 py-2 text-left text-sm
              text-gray-900
              hover:bg-gray-50
            `}
          >
            <span
              className={`
                flex h-4 w-4 shrink-0 items-center justify-center text-[#5C6BFF]
              `}
            >
              {selected && (
                <FontAwesomeIcon icon={faCheck} className="text-xs" />
              )}
            </span>
            {option.title}
          </button>
        );
      })}
    </div>
  );
};
